import { animate, animateSequence, stagger } from 'vendor';

/**
 * Gedrag en beweging voor de Techweise-navigatie.
 *
 * Alles hieronder hangt aan de dialog-events van het theme en patcht hooguit één
 * methode per element. Geen enkel vendor-bestand is aangepast.
 *
 * `dialog:before-show` en `dialog:before-hide` bubbelen niet (theme.js dispatcht
 * ze zonder `bubbles`), maar de capture-fase loopt altijd van document naar
 * target. Eén capturing listener vangt daarmee elk paneel, ook na een
 * section-reload in de editor. Onze listener draait bovendien vóór die van het
 * theme (dat registreert op het element zelf, dus in de target-fase) — daar
 * maken we gebruik van om de paneelstaat te herstellen vóórdat het theme zijn
 * hoofdpaneel toont.
 */

const MEGA_CONTENT = '.tw-navmenu__card, .tw-navmenu__list, .tw-navmenu__footer-link';
const MEGA_BANNER = '.tw-navmenu__banner';
const DRAWER_SHEET = '[data-main-panel] .tw-navdrawer__sheet';

/** Hoe lang de muis op een menu-item moet blijven voordat het paneel opent. */
const HOVER_DREMPEL_MS = 120;

/** Binnen dit venster geldt een sluiting als "wissel naar een ander paneel". */
const WISSEL_VENSTER_MS = 150;

const beweegtGraag = () => window.matchMedia('(prefers-reduced-motion: no-preference)').matches;

// Per element de lopende reeks, zodat snel open/dicht/open geen twee tijdlijnen
// over hetzelfde element laat lopen.
const lopend = new WeakMap();

let laatsteShowPaneel = null;
let laatsteShowTijd = 0;
let laatsteHideTijd = 0;
let laatsteActivatorId = null;
let laatsteActivatorTijd = 0;

/** Zijn we bezig met een wissel tussen twee menu-items? */
function wisseltNu(paneel) {
  const nu = performance.now();
  if ([...document.querySelectorAll('.tw-navmenu[open]')].some((p) => p !== paneel)) return true;
  if (laatsteShowPaneel && laatsteShowPaneel !== paneel && nu - laatsteShowTijd < WISSEL_VENSTER_MS) return true;
  // De muis is net op een ánder menu-item beland. Dit is het betrouwbaarste
  // signaal: het theme sluit het oude paneel via een pointerenter op een
  // tussenliggend element, dus op het moment dat de sluiting plaatsvindt staat
  // er al niets meer open en is het nieuwe paneel nog niet getoond.
  if (laatsteActivatorId && laatsteActivatorId !== paneel.id && nu - laatsteActivatorTijd < WISSEL_VENSTER_MS) return true;
  return false;
}

/* ------------------------------------------------------------------ *
 * Mega-menu: geen crossfade bij het wisselen van paneel
 * ------------------------------------------------------------------ */

/**
 * Het theme fadet het oude paneel in 0,2s uit terwijl het nieuwe in 0,2s
 * infadet. Twee panelen van elk ~50% dekken samen maar 74%, dus tijdens elke
 * wissel schijnt de hero er doorheen met de tekst van beide panelen over
 * elkaar. Gemeten: 34% van de frames toonde meer dan één paneel, met pieken van
 * drie tegelijk. De panelen zijn ook nog verschillend breed (1327/724/888) op
 * dezelfde x, dus je ziet verticale randen opdoemen die er ervoor en erna niet
 * zijn.
 *
 * `createLeaveAnimationControls` is een methode op de klasse, dus je kunt hem
 * per instantie vervangen zonder theme.js aan te raken. Bij een wissel sluiten
 * we direct; beweeg je gewoon weg, dan houden we de nette fade van het theme.
 */
function patchVerlaten(paneel) {
  if (paneel.__twVerlatenGepatcht) return;
  paneel.__twVerlatenGepatcht = true;

  const origineel = paneel.createLeaveAnimationControls.bind(paneel);

  // ALTIJD direct sluiten, zonder voorwaarde. Eerder stond hier een
  // wissel-detectie zodat wegbewegen nog een nette fade van 0,2s kreeg, maar die
  // detectie is principieel onbetrouwbaar: het theme sluit het oude paneel via
  // een pointerenter op een tussenliggend element, dus op dat moment weet je nog
  // niet of er een ander item volgt. Bij een wissel liep er daardoor alsnog een
  // fade van 23 frames, en dat is precies de crossfade waar dit tegen moest
  // beschermen. Een mega-menu dat meteen weg is leest prima; het risico van
  // slimme detectie weegt hier niet op tegen 0,2s uitfaden.
  //
  // En bewust een no-op op `opacity`, géén `[1, 0]`: Motion commit de eindwaarde
  // als inline stijl. Die blijft staan, en dan opent het paneel de volgende keer
  // onzichtbaar. Het theme zet vlak hierna zelf `display: none`; wij hoeven
  // alleen een reeks te geven die direct settelt zodat `dialog:after-hide` vuurt.
  paneel.createLeaveAnimationControls = function () {
    return animateSequence([[paneel, { opacity: [1, 1] }, { duration: 0 }]]);
  };
}

function patchAlleMegaPanelen() {
  document.querySelectorAll('.tw-navmenu').forEach(patchVerlaten);
}

/* ------------------------------------------------------------------ *
 * Mega-menu: instagger van de inhoud
 * ------------------------------------------------------------------ */

/**
 * `HeaderMegaMenu` staggert `.mega-menu__navigation > *` en `.menu-promo`. Die
 * selectors raken onze markup niet, en overnemen kan niet:
 * `.mega-menu:has(.menu-promo)` zet `padding-inline-end: 0` en
 * `.mega-menu__navigation` is een flex-wrap met eigen gaps — dat sloopt de
 * ontwerp-kolommen. Vandaar een eigen reeks met dezelfde idiomen en timing.
 */
function megaMenu(paneel) {
  patchVerlaten(paneel);
  // Voor de openende kant is `wisseltNu` niet bruikbaar: die vergelijkt met het
  // eigen menu-item, en dat is hier per definitie het item waar de muis net op
  // is beland. Het signaal dat wél klopt is dat er zojuist een paneel gesloten
  // is — dan vervangen we er een, in plaats van uit het niets te openen.
  const wisselt = performance.now() - laatsteHideTijd < 250;
  laatsteShowPaneel = paneel;
  laatsteShowTijd = performance.now();

  // Bij een wissel hoort het vlak er meteen te staan. Het theme fadet het paneel
  // altijd van 0 naar 1, en bij een wissel gaat dezelfde plek dus 1 -> 0 -> 1:
  // je kijkt 0,2s lang door een half-doorzichtig wit vlak naar de hero, en juist
  // dat leest als een flits. Bij een verse opening blijft de fade wél staan,
  // want daar komt het paneel uit het niets.
  // De enter-animatie van het theme bestaat pas ná deze listener (theme.js:2237
  // draait na de dispatch op 2231), vandaar de microtask. Annuleren zet het
  // paneel terug op zijn eigen stijl, dus zichtbaar; de instagger van de inhoud
  // loopt gewoon door.
  // Ook bij reduced motion: dan hoort er helemaal geen fade te zijn, en die van
  // het theme staat buiten zijn eigen motion-check.
  // `finish()` en niet `cancel()`. Annuleren laat het paneel terugvallen op de
  // laatst vastgelegde waarde, en dat is de `opacity: 0` die de uitfade van het
  // theme als inline stijl achterliet — het paneel opende dan volledig
  // onzichtbaar terwijl het wél [open] was en klikken opving. `finish()` springt
  // naar het eind van de reeks, dus naar opacity 1.
  if (wisselt || !beweegtGraag()) {
    queueMicrotask(() => paneel.getAnimations().forEach((a) => a.finish()));
  }

  // Sluit een ánder open paneel via zijn eigen `hide()`, zodat het theme zijn
  // normale route loopt: leave-animatie -> `dialog:after-hide` -> `display: none`.
  // De override hierboven maakt die leave instant, dus er is geen crossfade.
  //
  // NIET met een eigen `animate(ander, { opacity: 0 })` erbovenop. Dat draait
  // over dezelfde property als de lopende leave-animatie, waardoor Motion die
  // annuleert, de `finished`-promise nooit settelt, het theme `after-hide` nooit
  // dispatcht en `display: none` dus nooit wordt gezet. Er bleef dan een
  // onzichtbaar paneel van 1327x368 staan met `pointer-events: auto`, dat
  // klikken op de pagina eronder opving — een klik op de hero navigeerde naar
  // een willekeurige productpagina.
  document.querySelectorAll('.tw-navmenu[open]').forEach((ander) => {
    if (ander === paneel) return;
    lopend.get(ander)?.cancel();
    ander.hide();
  });

  lopend.get(paneel)?.cancel();
  if (!beweegtGraag()) return;

  const inhoud = paneel.querySelectorAll(MEGA_CONTENT);
  if (inhoud.length === 0) return;

  const stappen = [
    [
      inhoud,
      { opacity: [0, 1], transform: ['translateY(1em)', 'translateY(0)'] },
      // startDelay 0.1 = waar de paneelfade van het theme (0.2s, eigen tijdlijn)
      // zijn `at: "-0.1"` zou hebben; zo lopen beide gelijk.
      // Bij een wissel staat het vlak er meteen, dus een startvertraging levert
      // een leeg wit paneel op — gemeten 17 frames (~130ms) zonder één element.
      // Alleen bij een verse opening loopt de vertraging gelijk op met de fade.
      { duration: 0.2, delay: stagger(0.05, { startDelay: wisselt ? 0 : 0.1 }), ease: 'easeInOut' },
    ],
  ];

  const banner = paneel.querySelectorAll(MEGA_BANNER);
  if (banner.length > 0) {
    stappen.push([banner, { opacity: [0, 1] }, { duration: 0.3, at: '-0.1', ease: 'easeInOut' }]);
  }

  lopend.set(paneel, animateSequence(stappen));
}

/* ------------------------------------------------------------------ *
 * Snelheid van de mobiele lade
 * ------------------------------------------------------------------ */

/**
 * Het theme animeert de lade royaal: de container schuift in 0,35s binnen, een
 * subpaneel in 0,4s, de terugweg in 0,25s, en de inhoud van het geopende paneel
 * begint pas na een startvertraging van 0,4s. Op een echte telefoon telt dat op
 * tot een menu dat "traag" aanvoelt: tikken, wachten, tikken, wachten
 * (merchant-melding 26-08-2026).
 *
 * Hieronder staan alle duren op de helft of minder. 150 tot 200ms is het
 * gebied waar een beweging als direct leest en toch niet schokt; korter dan
 * ~120ms ziet een schuif eruit als een sprong. De easing gaat naar een
 * uitlopende curve (snel weg, zacht landen), zodat het kort én rustig oogt.
 *
 * We vervangen de methodes per instantie, net als bij het mega-menu. Geen
 * vendor-bestand aangeraakt, en het contract van het theme blijft intact: de
 * beloftes van `show()` en `hide()` settelen altijd, want `onSwitchToPanel_fn`
 * zet `_isTransitioningPanel` alleen terug in hun `Promise.all(...).then(...)`.
 * Een belofte die nooit settelt is hier een permanent dood menu.
 */
const T = {
  ladeIn: 0.16,      // theme 0.35 — container schuift binnen
  ladeUit: 0.16,     // theme 0.25
  overlay: 0.12,     // theme 0.2 / 0.25
  inhoud: 0.14,      // theme 0.2 met startDelay 0.4
  subIn: 0.24,       // theme 0.4 — subpaneel schuift van rechts binnen
  subUit: 0.24,      // theme 0.25 — subpaneel schuift naar rechts weg
  hoofdIn: 0.24,     // theme 0.25 — hoofdpaneel komt terug
  hoofdUit: 0.24,    // theme 0.4 — hoofdpaneel wijkt naar links
};
const EASE_UIT = [0.22, 0.61, 0.36, 1];   // snel weg, zacht landen
// De terugweg (niveau 2 naar 1) is één beweging in drie sporen: het subpaneel
// schuift weg, het hoofdpaneel komt terug en de hoogte loopt mee. Die drie
// moeten dezelfde duur en dezelfde curve hebben, anders stond het subpaneel
// eerst stil (easeIn) en schoot het daarna weg terwijl het hoofdpaneel al
// bewoog en de hoogte nog 40ms naliep: snel, maar schokkerig (merchant
// 27-08-2026). Daarom subUit = hoofdIn = subIn = hoofdUit en overal EASE_UIT.
// 0,24s in plaats van 0,18: op een echte telefoon las 180ms met deze curve
// als een tik (het meeste pad zit in de eerste 80ms); 240ms is nog steeds
// direct, maar geeft 14 frames om de schuif te zien. Figma heeft hiervoor
// geen spec (alleen statische frames per menustaat), dit is een keuze.
// De panelen animeren alleen `transform`: de donkere waas van het theme op
// het hoofdpaneel (een `background`-animatie) betekent een volledige
// repaint van dat paneel per frame, op een telefoon met dpr 3 precies wat
// hapert; de parallax (-18%) geeft de diepte al.
const HOOFD_VERSCHUIVING = 18;            // theme 30% — korter pad, dus minder "leeg" links

/** Motion geeft controls terug; `.finished` is de belofte, met terugval. */
const klaar = (controls) => (controls && controls.finished) || Promise.resolve(controls);

/** De lade zelf: binnenschuiven en sluiten. */
function versnelLade(drawerEl) {
  if (drawerEl.__twSnel) return;
  drawerEl.__twSnel = true;

  drawerEl.createEnterAnimationControls = function () {
    const stappen = [[this.getShadowPartByName('overlay'), { opacity: [0, 1] }, { duration: T.overlay }]];
    if (beweegtGraag()) {
      stappen.push([
        this.getShadowPartByName('content'),
        { transform: ['translateX(calc(var(--transform-logical-flip) * -100%))', 'translateX(0)'] },
        { duration: T.ladeIn, at: '<', ease: EASE_UIT },
      ]);
    } else {
      stappen.push([this.getShadowPartByName('content'), { opacity: [0, 1] }, { duration: T.overlay, at: '<' }]);
    }
    return animateSequence(stappen);
  };

  drawerEl.createLeaveAnimationControls = function () {
    const stappen = [[this.getShadowPartByName('overlay'), { opacity: [1, 0] }, { duration: T.overlay }]];
    if (beweegtGraag()) {
      stappen.push(
        [this.shadowRoot.querySelector('slot').assignedElements(), { opacity: [1, 0] }, { duration: 0.1, ease: 'easeInOut', at: '<' }],
        [this.getShadowPartByName('content'), { transform: ['translateX(0)', 'translateX(calc(var(--transform-logical-flip) * -100%))'] }, { duration: T.ladeUit, ease: 'easeIn', at: '<' }]
      );
    } else {
      stappen.push([this.getShadowPartByName('content'), { opacity: [1, 0], at: '<' }, { duration: T.overlay }]);
    }
    return animateSequence(stappen);
  };
}

/**
 * De panelen: openen, wisselen en terug.
 *
 * `show({ initial: true })` doet bewust NIETS met de inhoud meer. Dat is de
 * 0,4s-startvertraging van het theme, en `drawer()` hieronder animeert het
 * sheet zelf, korter en zonder wachttijd. Twee tijdlijnen over hetzelfde
 * element zouden elkaar annuleren.
 */
function versnelPaneel(paneel) {
  if (paneel.__twSnel) return;
  paneel.__twSnel = true;

  paneel.show = async function ({ initial = true } = {}) {
    this.classList.add('is-visible');
    if (!beweegtGraag()) return klaar(animate(this, { opacity: [0, 1] }, { duration: 0.1 }));
    if (initial) return Promise.resolve();

    const flip = document.dir === 'rtl' ? -1 : 1;
    if (this.hasAttribute('data-main-panel')) {
      // De terugweg: alleen transform (zie de noot bij T), geen waas.
      return klaar(
        animate(this, { transform: [`translateX(${-HOOFD_VERSCHUIVING * flip}%)`, 'translateX(0)'] }, { duration: T.hoofdIn, ease: EASE_UIT })
      );
    }
    return klaar(animate(this, { transform: [`translateX(${100 * flip}%)`, 'translateX(0)'] }, { duration: T.subIn, ease: EASE_UIT }));
  };

  paneel.hide = async function ({ instant = false } = {}) {
    let belofte = Promise.resolve();
    if (!instant) {
      const flip = document.dir === 'rtl' ? -1 : 1;
      if (!beweegtGraag()) {
        belofte = klaar(animate(this, { opacity: [1, 0] }, { duration: 0.1 }));
      } else if (this.hasAttribute('data-main-panel')) {
        belofte = klaar(
          animate(this, { transform: ['translateX(0)', `translateX(${-HOOFD_VERSCHUIVING * flip}%)`] }, { duration: T.hoofdUit, ease: EASE_UIT })
        );
      } else {
        belofte = klaar(animate(this, { transform: ['translateX(0%)', `translateX(${100 * flip}%)`] }, { duration: T.subUit, ease: EASE_UIT }));
      }
    }
    // Zelfde afronding als het theme: pas ná de animatie het paneel opruimen,
    // in een frame zodat de laatste stand nog één keer getekend is. De belofte
    // moet hoe dan ook settelen, ook als de animatie wordt afgebroken.
    const opruimen = () =>
      new Promise((klaarMee) => {
        requestAnimationFrame(() => {
          this.style = '';
          this.classList.remove('is-visible');
          klaarMee();
        });
      });
    return belofte.then(opruimen, opruimen);
  };
}

/* ------------------------------------------------------------------ *
 * Mobiel paneel
 * ------------------------------------------------------------------ */

/**
 * Herstel de paneelstaat vóór het theme zijn hoofdpaneel toont.
 *
 * `onAfterHide_fn2` (theme.js:5542) is de énige plek die de panelen reset, en
 * die hangt aan `dialog:after-hide`. Dat event komt alleen als de leave-animatie
 * zijn promise oplost, en `show()` annuleert die animatie (theme.js:2223).
 * Heropen je dus binnen de sluitanimatie van 409ms — een pauze van 100 tot 400ms
 * volstaat, snelheid speelt geen rol — dan wordt er nooit gereset. Gevolg:
 * hoofdpaneel én subpaneel staan tegelijk op `is-visible` in dezelfde grid-cel,
 * en omdat de terugknop dan naar het paneel wijst dat al `fromPanel` is, lopen
 * `hide()` en `show()` op hetzelfde element. Motion annuleert de hide, die
 * promise settelt nooit, `_isTransitioningPanel` blijft `true` en het menu
 * reageert nergens meer op tot je het sluit en opnieuw opent.
 *
 * Wij doen die reset zelf, in de capture-fase, dus vóór `onBeforeShow_fn`.
 */
function herstelPanelen(drawerEl) {
  // Synchroon, niet via `paneel.hide({instant:true})`. Die methode ruimt op in
  // `promise.then(() => frame.render(...))`, dus in een microtask plús een
  // frame — dat draait ná `onBeforeShow_fn` het hoofdpaneel heeft getoond en
  // haalt `is-visible` er dan meteen weer af. Resultaat: een leeg menu.
  drawerEl.querySelectorAll('header-menu-drawer-panel').forEach((paneel) => {
    // `finish()` en NOOIT `cancel()`. `onSwitchToPanel_fn` (theme.js:5544) zet
    // `_isTransitioningPanel = true` en zet die alleen terug in
    // `Promise.all([fromPanel.hide(), toPanel.show()]).then(...)`. Annuleer je een
    // animatie waar die beloften op wachten, dan settelen ze nooit, blijft de vlag
    // staan en weigert de guard vanaf dat moment ELKE paneelwissel — permanent,
    // alleen te herstellen met een paginaherlaadbeurt. Dat was een ergere versie
    // van precies de race die deze reset moest oplossen. `finish()` springt naar
    // het eind, dus de beloften settelen en de vlag gaat netjes terug.
    paneel.getAnimations().forEach((a) => {
      try { a.finish(); } catch { /* animatie zonder eindpunt: laat staan */ }
    });
    paneel.classList.remove('is-visible');
    paneel.removeAttribute('style');
  });
}

/**
 * De geërfde startvertraging van 0,4s weghalen.
 *
 * `HeaderMenuDrawerPanel.show()` animeert `.menu-drawer__panel-body > *` met
 * `stagger(0.1, { startDelay: 0.4 })`, en ons sheet is precies dat ene kind. Die
 * 0,4s hoort in het theme ná de zijwaartse slide van de container te komen; bij
 * ons ís het sheet de inhoud, dus de paneelinhoud kwam pas na ~500ms in beeld
 * terwijl de container al na 350ms stond.
 */
function drawer(drawerEl) {
  versnelLade(drawerEl);
  drawerEl.querySelectorAll('header-menu-drawer-panel').forEach(versnelPaneel);
  herstelPanelen(drawerEl);

  // Vangnet tegen een open maar LEGE lade. Een `hide()` die nog liep ruimt op in
  // `promise.then(() => frame.render(...))`, dus uitgesteld met een microtask én
  // een frame. Door het `finish()` hierboven settelt die belofte alsnog, en
  // vervolgens haalt die opruiming `is-visible` weg bij het paneel dat het theme
  // inmiddels heeft getoond. Resultaat: lade open, geen enkel paneel zichtbaar.
  //
  // Bewust alleen herstellen als er niets zichtbaar is — nooit het hoofdpaneel
  // afdwingen, want dan zou dit een legitieme paneelwissel in de eerste frames
  // terugdraaien.
  const borgStaat = () => {
    if (!drawerEl.hasAttribute('open')) return;
    if (drawerEl.querySelector('header-menu-drawer-panel.is-visible')) return;
    drawerEl.querySelector('[data-main-panel]')?.classList.add('is-visible');
  };
  requestAnimationFrame(() => {
    borgStaat();
    requestAnimationFrame(borgStaat);
  });

  if (!beweegtGraag()) return;

  // De listener van het theme zit op het element zelf en draait dus in de
  // target-fase, ná deze capture-listener maar binnen dezelfde dispatch. Een
  // microtask erna bestaat de animatie van het theme en kunnen we hem overnemen.
  queueMicrotask(() => {
    const sheet = drawerEl.querySelector(DRAWER_SHEET);
    if (!sheet) return;

    // Ook hier `finish()` en geen `cancel()`. Deze reeks wordt weliswaar door
    // niemand afgewacht (het theme roept `show({initial:true})` aan zonder await),
    // maar annuleren van een animatie waar het theme op zou kunnen wachten is de
    // oorzaak geweest van twee permanente vastlopers deze ronde. Geen enkele
    // uitzondering meer. `finish()` laat het sheet op opacity 1 staan; onze eigen
    // animatie hieronder zet hem daarna alsnog van 0 naar 1.
    sheet.getAnimations().forEach((a) => {
      try { a.finish(); } catch { /* geen eindpunt: laat staan */ }
    });
    lopend.get(sheet)?.cancel();

    lopend.set(
      sheet,
      animate(
        sheet,
        { opacity: [0, 1], transform: ['translateY(1em)', 'translateY(0)'] },
        // Landt rond 160ms, net wanneer de lade zijn slide afrondt: de inhoud
        // staat er zodra het paneel tot stilstand komt.
        { duration: T.inhoud, delay: 0.02, ease: EASE_UIT }
      )
    );
  });
}

/* ------------------------------------------------------------------ *
 * Mobiel paneel: de zichtbare hoogte mee laten bewegen bij een panelwissel
 * ------------------------------------------------------------------ */

/**
 * De panelen liggen alle in dezelfde grid-cel (`theme.css:6870-6881`), dus de
 * container is zo hoog als het hoogste zichtbare paneel. Bij een wissel sprong
 * de onderrand van het witte sheet daardoor in **één frame** van de ene naar de
 * andere hoogte (gemeten 638 naar 1231, en per categorie anders) terwijl de
 * panelen er langer over doen om langs elkaar te schuiven.
 *
 * Geen enkele layout-maat wordt geanimeerd. De container is een shadow-part:
 * een gewone inline `height` daarop verliest van de `::part(content)`-regel uit
 * het document (bij normale declaraties wint de buitenste tree) en wordt
 * genegeerd. De container is bovendien transparant; wat je ziet is alleen de
 * onderrand van het sheet. Die laten we meelopen met een `clip-path: inset()`
 * op het paneel dat de hoogte bepaalt. Dat raakt geen layout, dus de
 * scrollcontainer verandert pas van maat als het uitgaande paneel aan het
 * eind verdwijnt, en dat is dan al onzichtbaar.
 *
 * Valkuil die hier twee dagen heeft gekost (opgelost 27-08-2026 met
 * WebKit-opnames): `overflow-x: clip` op `x-header` tijdens de schuif, ooit
 * bedoeld tegen een horizontale scrollbar. WebKit (iOS Safari) rekent
 * `overflow-x: clip` met `overflow-y: visible` als clip op béíde assen, en de
 * lade hangt ónder de balk, dus de hele lade werd 6 tot 8 frames weggeknipt:
 * "een donker vlak en dan pas niveau 1". In Chrome onzichtbaar. De container
 * heeft inmiddels zelf `overflow-x: hidden`; gemeten per frame op 390 en 768:
 * 0 horizontale overflow zonder die clip. Zet hem dus nooit terug.
 */
const SCHUIF_MS = T.subIn * 1000;
const SCHUIF_EASE = EASE_UIT;

let wisselTeller = 0;

function paneelWisselHoogte(drawerEl, doelId) {
  const mijnBeurt = ++wisselTeller;
  const container = drawerEl.getShadowPartByName?.('content') || drawerEl.shadowRoot?.querySelector('[part~="content"]');
  if (!container || !beweegtGraag()) return;

  // Deze listener loopt in de capture-fase, vóór het theme; het enige zichtbare
  // paneel is nu nog het uitgaande.
  const uitgaand = drawerEl.querySelector('.menu-drawer__panel.is-visible');
  const doel = doelId ? drawerEl.querySelector(`#${CSS.escape(doelId)}`) : null;
  if (!uitgaand || !doel || uitgaand === doel) return;

  const van = container.getBoundingClientRect().height;
  if (!van) return;

  requestAnimationFrame(() => {
    if (mijnBeurt !== wisselTeller) return;
    // Meet het sheet van het doelpaneel, niet het paneel zelf: de panelen liggen
    // in dezelfde grid-cel en rekken mee met de hoogste, dus het doelpaneel
    // rapporteert tijdens de overgang de hoogte van zijn buurman.
    const sheet = doel.querySelector('.tw-navdrawer__sheet');
    const naar = sheet ? sheet.getBoundingClientRect().height : van;
    if (Math.abs(naar - van) < 2) return;

    // Krimpt de lade, dan bepaalt het uitgaande paneel de hoogte (het inkomende
    // is al `naar`); groeit hij, dan het inkomende (het uitgaande is `van`).
    // `round` houdt de hoeken van het sheet tijdens de clip.
    const krimpt = naar < van;
    const drager = krimpt ? uitgaand : doel;
    const paneelHoogte = drager.getBoundingClientRect().height || Math.max(van, naar);
    const inset = (zichtbaar) => `inset(0 0 ${Math.max(0, Math.round(paneelHoogte - zichtbaar))}px 0 round var(--tw-radius-lg, 8px))`;
    drager.style.clipPath = inset(van);
    void drager.offsetHeight; // reflow, zodat de transition vanaf `van` vertrekt
    drager.style.transition = `clip-path ${SCHUIF_MS}ms cubic-bezier(${SCHUIF_EASE.join(',')})`;
    drager.style.clipPath = inset(naar);

    let afgerond = false;
    const klaarMee = (event) => {
      if (event && (event.target !== drager || event.propertyName !== 'clip-path')) return;
      if (afgerond) return;
      afgerond = true;
      drager.removeEventListener('transitionend', klaarMee);
      // Het uitgaande paneel niet zelf leegmaken: zijn `hide()` wist alle inline
      // stijlen en verbergt het paneel in één stap; eerder de clip weghalen
      // toont één frame de volle oude hoogte.
      if (!krimpt && drager.classList.contains('is-visible')) {
        drager.style.clipPath = '';
        drager.style.transition = '';
      }
    };
    drager.addEventListener('transitionend', klaarMee);
    // Vangnet: geen transitionend (tab op de achtergrond, afgebroken wissel).
    setTimeout(() => klaarMee(), SCHUIF_MS + 80);
  });
}

document.addEventListener(
  'click',
  (event) => {
    const knop = event.target instanceof Element ? event.target.closest('[data-panel-toggle]') : null;
    if (!knop) return;
    const drawerEl = knop.closest('header-menu-drawer.tw-navdrawer');
    if (drawerEl) paneelWisselHoogte(drawerEl, knop.getAttribute('aria-controls'));
  },
  true
);

/* ------------------------------------------------------------------ *
 * Hover-drempel op de topnav
 * ------------------------------------------------------------------ */

/**
 * Het theme opent een mega-menu bij `pointerenter` zonder enige drempel
 * (theme.js:2523): gemeten 5 tot 7ms van binnenkomst tot openen. Beweeg je je
 * muis over de navbalk — op weg naar het logo, het zoekicoon of het
 * winkelmandje — dan klappen alle drie de panelen achter elkaar open. En elke
 * opening kost een geforceerde documentbrede style-recalc (theme.js:2231 roept
 * `getComputedStyle` aan terwijl `[open]` net gezet is), goed voor ~1150 van de
 * 2300 elementen. Acht keer heen en weer vegen leverde 33 van die recalcs op,
 * samen 685ms hoofddraadwerk.
 *
 * We houden het `pointerenter`-event in de capture-fase tegen en laten hem pas
 * door als de muis er echt even blijft. De her-dispatch gaat naar hetzelfde
 * element, zodat het theme daarna zijn eigen volledige route loopt — inclusief
 * het registreren van de sluit-bij-verlaten-listener. We nemen dus niets over
 * wat het theme al doet.
 */
let hoverTimer = null;
let hoverDoel = null;
let laatDoor = false;

function isActivator(el) {
  return el instanceof Element && typeof el.matches === 'function' && el.matches('[data-open-on-hover][aria-controls]');
}

document.addEventListener(
  'pointerenter',
  (event) => {
    if (laatDoor) return; // onze eigen her-dispatch: doorlaten
    const el = event.target;
    if (!isActivator(el)) return;

    const nu = performance.now();
    laatsteActivatorId = el.getAttribute('aria-controls');
    laatsteActivatorTijd = nu;

    // Zit de bezoeker al in het menu, dan moet de wissel direct gebeuren.
    // Wachten is hier juist lelijk: het theme sluit het oude paneel meteen bij
    // binnenkomst, dus tijdens de drempel kijk je door een half weggefadet
    // paneel naar de hero. Let op dat je niet alleen op "staat er iets open"
    // toetst — het theme sluit het oude paneel via een pointerenter op een
    // tussenliggend element, dus dat is dan al weg. Vandaar ook laatsteHideTijd.
    if (document.querySelector('.tw-navmenu[open]') || nu - laatsteHideTijd < 300) return;

    event.stopPropagation();

    clearTimeout(hoverTimer);
    hoverDoel = el;
    hoverTimer = setTimeout(() => {
      // Nog steeds hetzelfde item én de muis hangt er nog boven?
      if (hoverDoel !== el || !el.matches(':hover')) return;
      laatDoor = true;
      el.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false, cancelable: false }));
      laatDoor = false;
    }, HOVER_DREMPEL_MS);
  },
  true
);

document.addEventListener(
  'pointerleave',
  (event) => {
    if (event.target === hoverDoel) {
      clearTimeout(hoverTimer);
      hoverDoel = null;
    }
  },
  true
);

/* ------------------------------------------------------------------ *
 * Aansluiten
 * ------------------------------------------------------------------ */

document.addEventListener(
  'dialog:before-show',
  (event) => {
    const el = event.target;
    if (!(el instanceof Element)) return;
    if (el.classList.contains('tw-navmenu')) megaMenu(el);
    else if (el.classList.contains('tw-navdrawer')) drawer(el);
  },
  true
);

// De instagger liep door terwijl het paneel al uitfadede: de inhoud schoof nog
// naar binnen terwijl het paneel verdween.
document.addEventListener(
  'dialog:before-hide',
  (event) => {
    const el = event.target;
    if (el instanceof Element && el.classList.contains('tw-navmenu')) {
      laatsteHideTijd = performance.now();
      lopend.get(el)?.cancel();
    }
  },
  true
);

// Een open mobiel paneel bleef bij een resize naar desktop staan als een witte
// kolom van 664px naast de werkende desktopnavigatie, met een klikvanger van
// 1328x0 — dus klik-buiten-sluiten deed niets en alleen Escape hielp. In
// landschap werd het paneel breder dan het scherm hoog was. Het theme sluit niet
// bij een breekpuntwissel; wij wel.
window.matchMedia('(min-width: 1000px)').addEventListener('change', () => {
  document.querySelectorAll('header-menu-drawer.tw-navdrawer[open]').forEach((d) => d.hide());
});

patchAlleMegaPanelen();
